import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { pickByLanguage } from '@/lib/language';
import type { AppLanguage } from '@/lib/language';

interface CancelDowngradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user confirms — after picking a reason */
  onConfirm: () => void;
  type: 'cancel' | 'downgrade';
  /** Target plan name, shown in downgrade copy */
  targetPlanName?: string;
  language: AppLanguage;
}

const CANCEL_REASONS = (t: (pt: string, en: string) => string) => [
  { id: 'too_expensive',      label: t('Muito caro para mim',                 'Too expensive for me') },
  { id: 'not_using',          label: t('Não estou usando os recursos',         'Not using the features') },
  { id: 'found_alternative',  label: t('Encontrei uma alternativa melhor',     'Found a better alternative') },
  { id: 'just_testing',       label: t('Estava apenas testando a plataforma',  'Just testing the platform') },
  { id: 'technical_issues',   label: t('Problemas técnicos',                   'Technical issues') },
  { id: 'other',              label: t('Outro motivo',                         'Other reason') },
];

const DOWNGRADE_REASONS = (t: (pt: string, en: string) => string) => [
  { id: 'reduce_cost',        label: t('Quero reduzir custos',                       'Want to reduce costs') },
  { id: 'too_many_features',  label: t('O plano atual tem mais do que preciso',       'Current plan has more than I need') },
  { id: 'temporary',          label: t('Vou precisar de menos recursos por um tempo', 'Temporarily need fewer features') },
  { id: 'financial',          label: t('Dificuldades financeiras',                    'Financial difficulties') },
  { id: 'found_alternative',  label: t('Encontrei uma alternativa melhor',            'Found a better alternative') },
  { id: 'other',              label: t('Outro motivo',                                'Other reason') },
];

export function CancelDowngradeDialog({
  open,
  onOpenChange,
  onConfirm,
  type,
  targetPlanName,
  language,
}: CancelDowngradeDialogProps) {
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });
  const [reason, setReason] = useState('');
  const [otherText, setOtherText] = useState('');

  const reasons = type === 'cancel' ? CANCEL_REASONS(t) : DOWNGRADE_REASONS(t);
  const isOther = reason === 'other';
  const canConfirm = reason !== '' && (reason !== 'other' || otherText.trim().length > 0);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setReason('');
      setOtherText('');
    }
    onOpenChange(next);
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    handleOpenChange(false);
    onConfirm();
  };

  const title =
    type === 'cancel'
      ? t('Por que você quer cancelar?', 'Why do you want to cancel?')
      : t('Por que você quer fazer downgrade?', 'Why do you want to downgrade?');

  const description =
    type === 'cancel'
      ? t(
          'Ao cancelar, você perderá acesso aos recursos premium ao fim do período atual. Você pode reativar a qualquer momento.',
          'By canceling, you will lose access to premium features at the end of the current period. You can reactivate at any time.'
        )
      : t(
          `Você está prestes a mudar para o plano ${targetPlanName ?? 'inferior'}. Essa alteração será aplicada no próximo ciclo de cobrança.`,
          `You are about to switch to the ${targetPlanName ?? 'lower'} plan. The change will apply on your next billing cycle.`
        );

  const confirmLabel =
    type === 'cancel'
      ? t('Sim, cancelar assinatura', 'Yes, cancel subscription')
      : t('Sim, fazer downgrade', 'Yes, downgrade');

  const keepLabel =
    type === 'cancel'
      ? t('Manter assinatura', 'Keep subscription')
      : t('Manter plano atual', 'Keep current plan');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">{description}</DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <p className="text-sm font-medium text-foreground mb-3">
            {t('Qual é o principal motivo?', "What's your main reason?")}
          </p>
          <RadioGroup value={reason} onValueChange={setReason} className="space-y-2">
            {reasons.map(({ id, label }) => (
              <div key={id} className="flex items-center gap-3 group">
                <RadioGroupItem
                  value={id}
                  id={`reason-${id}`}
                  className="shrink-0"
                />
                <Label
                  htmlFor={`reason-${id}`}
                  className="text-sm cursor-pointer text-muted-foreground group-has-[:checked]:text-foreground transition-colors"
                >
                  {label}
                </Label>
              </div>
            ))}
          </RadioGroup>

          {isOther && (
            <Textarea
              className="mt-3 text-sm resize-none"
              rows={3}
              placeholder={t('Descreva o motivo…', 'Describe your reason…')}
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              maxLength={500}
              autoFocus
            />
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            className="w-full sm:w-auto"
          >
            {keepLabel}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="w-full sm:w-auto"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
